import { createContext, useContext, useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";

import axios from "axios";
import { useCookies } from "react-cookie";
import { useDispatch } from "react-redux";

import { clearAllReturnHints } from "../components/Dashboard/Workspace/google/returnHint";
import { clearStoredPeriod } from "../components/FacilitatorShell/periods";
import { clearNamesConcealed } from "../components/FacilitatorShell/privacy";
import { setIsCohortUser } from "../store/user/slice";
import axiosInstance from "../utils/axiosInstance";

const AuthContext = createContext(null);

export const AuthProvider = ({ children, user: initialUser }) => {
  const dispatch = useDispatch();
  const [, setCookies, removeCookie] = useCookies(["token", "oldPassword"]);

  // Initialize state synchronously from initialUser to avoid redirect race condition
  const [user, setCurrentUser] = useState(() => initialUser || null);
  const [isAuthReady, setIsAuthReady] = useState(() => !!initialUser);
  const [isAdmin, setIsAdmin] = useState(() => {
    if (!initialUser) return false;
    const roleId = initialUser.role_id || initialUser.role?.id;
    return roleId == 4 || roleId === "4" || Number(roleId) === 4;
  });
  const router = useNavigate();

  const signin = async (email, password) => {
    try {
      const { data: loginData } = await axiosInstance("/auth/login", "post", {
        email,
        password,
      });

      const token = loginData.token;
      const expiresIn = loginData.expires_in;

      const { data: userData } = await axios.get(
        `${process.env.REACT_APP_API_URL}/users/me?include=cohortUser,role_id,squads,school`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const user = userData.data;

      setCurrentUser(user);
      dispatch(setIsCohortUser(!!user.cohortUser));

      setCookies("oldPassword", password, { maxAge: 3600 }); // 1 hour
      setCookies("token", token, { maxAge: expiresIn / 1000 });

      // Check for teacher role (role_id = 4)
      // Handle both number and string types, and nested role object
      const roleId = user.role_id || user.role?.id;
      const isTeacher = roleId == 4 || roleId === "4" || Number(roleId) === 4;

      if (isTeacher) {
        setIsAdmin(true);
        return router("/admin/dashboard");
      }

      if (user.cohortUser) {
        return router("/dashboard");
      }

      return router("/season");
    } catch (err) {
      console.error("Login error:", err);
      throw err;
    }
  };

  const handleCleverCallback = async (token, expiresIn) => {
    try {
      const { data: userData } = await axios.get(
        `${process.env.REACT_APP_API_URL}/users/me?include=cohortUser,role_id,squads,school`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const user = userData.data;

      setCurrentUser(user);
      dispatch(setIsCohortUser(!!user.cohortUser));

      setCookies("token", token, {
        maxAge: expiresIn ? parseInt(expiresIn) / 1000 : 3600,
      });

      // Check for teacher role (role_id = 4)
      // Handle both number and string types, and nested role object
      const roleId = user.role_id || user.role?.id;
      const isTeacher = roleId == 4 || roleId === "4" || Number(roleId) === 4;

      if (isTeacher) {
        setIsAdmin(true);
        return router("/admin/dashboard");
      }

      if (user.cohortUser) {
        return router("/dashboard");
      }

      return router("/season");
    } catch (err) {
      console.error("Clever callback error:", err);
      throw err;
    }
  };

  useEffect(() => {
    if (!initialUser) {
      setCurrentUser(null);
      setIsAdmin(false);
      setIsAuthReady(true);
      return;
    }
    // Check for teacher role (role_id = 4)
    // Handle both number and string types, and nested role object
    const roleId = initialUser.role_id || initialUser.role?.id;
    const isTeacher = roleId == 4 || roleId === "4" || Number(roleId) === 4;
    setIsAdmin(isTeacher);
    setCurrentUser(initialUser);
    setIsAuthReady(true);
    // Also update Redux state for cohort user (used in routing)
    dispatch(setIsCohortUser(!!initialUser.cohortUser));
  }, [initialUser, dispatch]);

  const signout = () => {
    clearNamesConcealed(user?.id);
    setCurrentUser(null);
    // The period filter is a per-person view preference held in sessionStorage.
    // Signing out on a shared machine must not leave the next facilitator's room
    // filtered by the previous one's class.
    clearStoredPeriod();
    // FE2: the Google OAuth return hints are per tab and per user; a shared
    // machine must not carry one learner's place to the next sign-in.
    clearAllReturnHints();
    removeCookie("token", { path: "/" });
    removeCookie("oldPassword", { path: "/" });
    dispatch({ type: "LOGOUT" });
  };

  const value = {
    signin,
    signout,
    handleCleverCallback,
    isAdmin,
    isAuthReady,
    initialUser,
    user,
    authenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
};

export const AuthConsumer = AuthContext.Consumer;
